import React from 'react';
import { MapPin, Navigation, Compass } from 'lucide-react';

interface VenueSectionProps {
  sede?: string;
  domicilio?: string;
  mapaEmbedUrl?: string | null;
}

export const VenueSection: React.FC<VenueSectionProps> = ({
  sede,
  domicilio,
  mapaEmbedUrl
}) => {
  const sedeNombre = sede?.trim() || 'Sede del Evento';
  const domicilioTexto = domicilio?.trim() || '';
  const querySearch = `${sedeNombre} ${domicilioTexto}`.trim();
  const queryMaps = encodeURIComponent(querySearch);
  const googleMapsUrl = `https://maps.google.com/?q=${queryMaps}`;

  // Extraer URL limpia de src="..." si se ingresó un snippet completo de <iframe> en la DB
  const extraerUrlEmbed = (input?: string | null): string | null => {
    if (!input || !input.trim()) return null;
    const match = input.match(/src=["']([^"']+)["']/i);
    if (match && match[1]) {
      return match[1];
    }
    return input.trim();
  };

  const rawEmbed = extraerUrlEmbed(mapaEmbedUrl);

  // Fallback automático a iframe embed de Google Maps por dirección/sede si mapaEmbedUrl no se configuró explícitamente
  const finalEmbedUrl = rawEmbed || (querySearch ? `https://maps.google.com/maps?q=${queryMaps}&t=&z=15&ie=UTF8&iwloc=&output=embed` : null);

  return (
    <section id="sede" className="py-24 bg-white relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4">
            <Compass className="w-3.5 h-3.5 text-brand-cyan" />
            Ubicación del Evento
          </div>
          <h2 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight">
            Sede del Congreso
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Lugar de encuentro oficial del evento.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Tarjeta de Información de la Sede */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 text-brand-cyan mb-4">
                <MapPin className="w-6 h-6 flex-shrink-0" />
                <h3 className="text-xl font-bold text-brand-navy">{sedeNombre}</h3>
              </div>

              {domicilioTexto ? (
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                  <span className="font-semibold text-brand-navy">Domicilio:</span> {domicilioTexto}
                </p>
              ) : (
                <p className="text-sm text-slate-500 mb-6 italic leading-relaxed">
                  Dirección disponible en la ubicación en mapa.
                </p>
              )}

              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full py-3.5 rounded-xl bg-brand-navy hover:bg-slate-800 text-white font-heading font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md hover:scale-[1.02]"
              >
                <Navigation className="w-4 h-4 text-brand-cyan" />
                <span>Abrir ubicación en Google Maps</span>
              </a>
            </div>
          </div>

          {/* Mapa Visual / Iframe Estilizado */}
          <div className="lg:col-span-7 h-[420px] rounded-3xl overflow-hidden border border-slate-200 shadow-xl relative bg-slate-100 flex items-center justify-center">
            {finalEmbedUrl ? (
              <iframe
                title={`Mapa ${sedeNombre}`}
                src={finalEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={false}
                loading="lazy"
              ></iframe>
            ) : (
              <div className="text-center p-6 text-slate-500">
                <MapPin className="w-12 h-12 text-brand-cyan mx-auto mb-3" />
                <p className="text-sm">Ubicación de sede pendiente de confirmación.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
